import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  DraggableList,
  Icon,
  List,
  ModalActionButton,
  ModalCancelButton,
  ModalContent,
  ModalContentActions,
  ModalContentBody,
  ModalContentFooter,
  ModalContentHeader,
  ModalOverlay,
  ModalPortal,
  ModalRoot,
  Switch,
  TooltipContent,
  TooltipRoot,
  TooltipTrigger,
  TruncatedText,
  useDraggableList,
} from '@socialchorus/shared-ui-components';
import MojoApiFactory from '../../../../config/api-factory';
import { AssistantResponse } from '../../../../models/assistant/interfaces/AssistantResponse';
import { Service } from '../../../../models/assistant/interfaces/subject/Service';
import { applyShortcutOrder, splitByPinned } from '../homepage-shortcuts';
import { createToastMessage, useToastMessages } from '../../toaster/toaster';
import Style from './edit-shortcut-order-modal.module.scss';

type ShortcutRowContentProps = {
  shortcut: AssistantResponse<Service>;
  checked: boolean;
  onCheckedChange?: (checked: boolean) => void;
};

const ShortcutRowContent = ({
  shortcut,
  checked,
  onCheckedChange,
}: ShortcutRowContentProps) => {
  const { t } = useTranslation();
  return (
    <>
      <img className={Style.Image} src={shortcut.subject.image_url} alt="" />
      <div className={Style.TextContent}>
        <div className={Style.NameRow}>
          <TruncatedText
            direction="vertical"
            lineClamp={1}
            className="text-body text-black-90"
          >
            {shortcut.name}
          </TruncatedText>
          {shortcut.subject.pinned && (
            <Icon size={16} className={Style.PinIcon}>
              push_pin
            </Icon>
          )}
        </div>
        {shortcut.subject.description && (
          <TruncatedText
            direction="vertical"
            lineClamp={1}
            className="text-caption text-black-60"
          >
            {shortcut.subject.description}
          </TruncatedText>
        )}
      </div>
      <div className={Style.ToggleContainer}>
        <Switch
          aria-label={t('shortcuts.toggle_visibility', { name: shortcut.name })}
          checked={checked}
          disabled={shortcut.subject.pinned}
          onCheckedChange={onCheckedChange}
        />
      </div>
    </>
  );
};

type EditShortcutOrderModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shortcuts: AssistantResponse<Service>[];
  homepageId: string;
  moduleId: string;
  currentOrder?: number[];
  currentHiddenShortcuts?: number[];
  moduleName: string;
};

export const EditShortcutOrderModal = ({
  open,
  onOpenChange,
  shortcuts,
  homepageId,
  moduleId,
  currentOrder,
  currentHiddenShortcuts,
  moduleName,
}: EditShortcutOrderModalProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { setToastMessage } = useToastMessages();

  // The lazy initializer is intentional: the parent only mounts this modal when
  // `editModalOpen` is true, so each open gets a fresh mount and fresh state.
  const [hiddenSet, setHiddenSet] = useState<Set<number>>(
    () => new Set(currentHiddenShortcuts ?? [])
  );

  const { pinnedShortcuts, nonPinnedShortcuts } = useMemo(() => {
    const { pinned, nonPinned } = splitByPinned(shortcuts);
    return {
      pinnedShortcuts: pinned,
      nonPinnedShortcuts: applyShortcutOrder(nonPinned, currentOrder),
    };
  }, [shortcuts, currentOrder]);

  const { items, handleDragEnd } = useDraggableList({
    initialItems: nonPinnedShortcuts,
    itemIdFn: (item) => item.id,
  });

  const handleToggleHidden = (id: number, checked: boolean) => {
    setHiddenSet((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const { mutate: saveOrder, isLoading: isSaving } = useMutation({
    mutationFn: async ({
      shortcutOrder,
      hiddenShortcuts,
    }: {
      shortcutOrder: number[];
      hiddenShortcuts: number[];
    }) => {
      return MojoApiFactory.optimus.put(
        `web_homepage/${homepageId}/modules/${moduleId}/user_customization`,
        {
          data: {
            shortcut_order: shortcutOrder,
            hidden_shortcuts: hiddenShortcuts,
          },
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['customHomepageConfig'],
      });
      queryClient.invalidateQueries({
        queryKey: ['customHomepageById', homepageId],
      });
      onOpenChange(false);
    },
    onError: () => {
      setToastMessage(
        createToastMessage({
          text: t('shortcuts.reorder_error', { name: moduleName }),
          type: 'error',
        })
      );
    },
  });

  const handleSave = () => {
    // Only non-pinned IDs are saved — pinned shortcuts are managed by admins
    // and always appear first regardless of user customization.
    const orderedIds = items
      .map((item) => Number(item.listItem.id))
      .filter((id) => !Number.isNaN(id));
    saveOrder({
      shortcutOrder: orderedIds,
      hiddenShortcuts: Array.from(hiddenSet),
    });
  };

  return (
    <ModalRoot open={open} onOpenChange={onOpenChange} mode="dialog">
      <ModalPortal>
        <ModalOverlay />
        <ModalContent size="medium">
          <ModalContentHeader
            className={Style.Header}
            title={t('shortcuts.edit_order', { name: moduleName })}
            description={t('shortcuts.edit_order_description', {
              name: moduleName,
            })}
          />
          <ModalContentBody>
            {pinnedShortcuts.length > 0 && (
              <List.Root className={Style.PinnedList} gap="md">
                {pinnedShortcuts.map((shortcut) => (
                  <TooltipRoot key={shortcut.id}>
                    <TooltipTrigger asChild>
                      <List.Item
                        className={Style.PinnedItem}
                        aria-disabled="true"
                      >
                        <Icon className={Style.DisabledDragHandle}>
                          drag_indicator
                        </Icon>
                        <ShortcutRowContent shortcut={shortcut} checked />
                      </List.Item>
                    </TooltipTrigger>
                    <TooltipContent
                      description={t('shortcuts.pinned_tooltip')}
                    />
                  </TooltipRoot>
                ))}
              </List.Root>
            )}
            <DraggableList.Root
              className={Style.List}
              droppableId="shortcut-order"
              onDragEnd={handleDragEnd}
              gap="md"
            >
              {items.map((item, index) => (
                <DraggableList.Item
                  key={item.listItem.id}
                  id={item.listItem.id}
                  index={index}
                  asChild
                >
                  <List.Item hoverable>
                    <Icon>drag_indicator</Icon>
                    <ShortcutRowContent
                      shortcut={item.listItem}
                      checked={!hiddenSet.has(Number(item.listItem.id))}
                      onCheckedChange={(checked) =>
                        handleToggleHidden(Number(item.listItem.id), checked)
                      }
                    />
                  </List.Item>
                </DraggableList.Item>
              ))}
            </DraggableList.Root>
          </ModalContentBody>
          <ModalContentFooter>
            <ModalContentActions>
              <ModalCancelButton label={t('common.cancel')} />
              <ModalActionButton
                label={t('shortcuts.save_changes')}
                onClick={handleSave}
                disabled={isSaving}
              />
            </ModalContentActions>
          </ModalContentFooter>
        </ModalContent>
      </ModalPortal>
    </ModalRoot>
  );
};
